export interface OrdersInterface extends Array<Order> {}

/**
 * Coordinates
 */
export interface Coordinates {
  type: "Point";
  coordinates: [number, number];
}

export interface LatLng {
  lat: number;
  lng: number;
}

export interface Location {
  latitude: number;
  longitude: number;
}

/**
 * Address
 */
export interface Address {
  street: string;
  city: string;
  zip: string;
  state: string;
  country: string;
}
export const DEFAULT_ADDRESS: Address = {
  street: "",
  city: "",
  zip: "",
  state: "",
  country: "",
};

/**
 * Shipping & Transportation
 */
export interface ShippingCompany {
  _id: string;
  tradeLicenseIssuer: string;
  name: string;
  address: Address
}

export interface Vehicle {
  _id: string;
  plateNumber: string;
  spId: string;
  formattedText?: string;
  plate?: Plate;
}
export interface VehicleFirst {
  _id: string;
  chassis: string;
  model: string;
  mulkiya: string;
  plateNumber: string;
  transportCompany: string;
  type: string;
  vehicleMake: string;
  plate?: Plate;
}
export const DEFAULT_VEHICLE_FIRST: VehicleFirst = {
  _id: "",
  chassis: "",
  model: "",
  mulkiya: "",
  plateNumber: "",
  transportCompany: "",
  type: "",
  vehicleMake: "",
};
export const DEFAULT_VEHICLE: Vehicle = {
  _id: "",
  plateNumber: "",
  spId: "",
  formattedText: "",
  plate: { emirates: "", country: "", code: "", number: "", type: "", _id: "" },
};

export interface Plate {
  emirates: string;
  country: string;
  code: string;
  number: string;
  type: string;
  _id: string;
}

/**
 * Users & Roles
 */
export interface User {
  _id: string;
  firstName: string;
  lastName: string;
  type: string;
  email: string;
  phone: string;
  company: any;
  organisation?: string;
  transportCompany?: { name: string };
  dlNumber?: string;
}
export const DEFAULT_USER: User = {
  _id: "",
  firstName: "",
  lastName: "",
  type: "",
  email: "",
  phone: "",
  company: "",
  transportCompany: { name: "" },
  dlNumber: "",
};

export interface Shipper {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  type: "shipper";
}

export const DEFAULT_SHIPPER: Shipper = {
  _id: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  type: "shipper"
};

/**
 * Employees, Drivers & Escorts
 */
export interface Employee {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  idNumber: string;
  idExpiry: string;
  siraId?: string;
  siraIdExpiry?: string;
  nationality: string;
  transportCompany: { name: string; tradeLicense: string };
  phone: string;
  photo: string;
  createdAt?: string;
}
export const DEFAULT_EMPLOYEE: Employee = {
  _id: "",
  firstName: "",
  lastName: "",
  email: "",
  idNumber: "",
  idExpiry: "",
  nationality: "",
  transportCompany: { name: "", tradeLicense: "" },
  phone: "",
  photo: "",
  createdAt: "",
};
export interface Driver {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  dlNumber: string;
  dlExpiry: string;
  nationality: string;
  transportCompany: { name: string; tradeLicense: string };
  phone: string;
  photo: string;
  createdAt?: string;
}
export const DEFAULT_DRIVER: Driver = {
  _id: "",
  firstName: "",
  lastName: "",
  email: "",
  dlNumber: "",
  dlExpiry: "",
  nationality: "",
  transportCompany: { name: "", tradeLicense: "" },
  phone: "",
  photo: "",
  createdAt: "",
};

export interface Escort {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  siraId: string;
  nationality: string;
  transportCompany: { name: string; tradeLicense: string };
  phone: string;
  photo: string;
  createdAt?: string;
}
export const DEFAULT_ESCORT: Escort = {
  _id: "",
  firstName: "",
  lastName: "",
  email: "",
  siraId: "",
  nationality: "",
  transportCompany: { name: "", tradeLicense: "" },
  phone: "",
  photo: "",
  createdAt: "",
};

/**
 * Orders
 */
export interface Order {
  _id: string;
  orderNumber: string;
  address?: { origin: string; destination: string };
  status: string;
  paymentStatus: string;
  rejectionReason: string[];
  origin: Coordinates;
  destination: Coordinates;
  shippingCompany: ShippingCompany;
  shipper: Shipper;
  possibleVehicles: Vehicle[];
  possibleDrivers: Driver[];
  possibleEscorts: Escort[];
  trips: number;
  validFrom: string;
  validTo: string;
  shipmentDetails: ShipmentDetail[];
  application: string;
  orderType: string;
  createdAt: string;
  approvedAt: string;
  startedAt?: string;
  updatedAt: string;
  completedAt?: string;
  eventDate: string;
  eventLayoutFilePath?: string;
  eventLetterFilePath?: string;
  siraApprovalFilePath?: string;
  lock?: string;
  escort?: Escort,
  driver?: Driver,
  vehicle?: Vehicle,
  imei?: string,
  batteryLevel?: number
}
export const DEFAULT_ORDER: Order = {
  _id: "",
  orderNumber: "",
  address: { origin: "", destination: "" },
  status: "",
  paymentStatus: "",
  rejectionReason: [],
  origin: { type: "Point", coordinates: [0, 0] },
  destination: { type: "Point", coordinates: [0, 0] },
  shippingCompany: { _id: "", tradeLicenseIssuer: "", name: "", address: DEFAULT_ADDRESS },
  shipper: DEFAULT_SHIPPER,
  possibleVehicles: [],
  possibleDrivers: [],
  possibleEscorts: [],
  trips: 0,
  validFrom: "",
  validTo: "",
  shipmentDetails: [],
  application: "",
  orderType: "",
  createdAt: "",
  startedAt: "",
  updatedAt: "",
  approvedAt: "",
  eventDate: "",
  lock: "",
  escort: DEFAULT_ESCORT,
  driver: DEFAULT_DRIVER,
  vehicle: DEFAULT_VEHICLE
};

/**
 * Shipment
 */
export interface ShipmentDetail {
  _id: string;
  description: string;
  hd: number;
  unSlNo: string;
  boxesQty: number;
  netWeight: number;
  grossWeight: number;
}
export const DEFAULT_SHIPMENT_DETAILS: ShipmentDetail[] = [];


export interface Inventory {
  _id: string;
  imei: string;
  batteryLevel: number;
  latitude: number,
  longitude: number,
  location: string,
  timestamp: string;
}

export interface PlateCodes {
  [key: string]: Array<{
    code: string;
    name: string;
  }>;
}

/**
 * Miscellaneous
 */
export interface Status {
  [key: string]: {
    name: string;
  };
}

export interface Sidebar {
  [key: string]: Array<{
    id: string;
    route: string;
    name: string;
    src: string;
    activeSrc?: string;
    showInTabs?: boolean;
  }>;
}

export interface UserHomePage {
  [key: string]: {
    route: string;
  };
}

export interface status {
  [key: string]: {
    name: string;
  };
}

export interface LocationState {
  fromRegister?: boolean;
  snackbarMessage?: string;
  snackbarSeverity?: "success" | "error" | "warning" | "info";
}

export interface InventoryItem {
  _id: string;
  imei: string;
  batteryLevel: number;
  latitude: number,
  longitude: number,
  location: string,
  timestamp: string;
}
